import useGroupConvertor, { type Group } from './group';

import type { Address } from '@/common/domain/Address';
import type { Contact } from '@/common/domain/Contact';
import { logTrace, SeverityLevel } from '@/common/utils/logger';
import useFleetConvertor, { type Fleet } from '@/fleet/domain/fleet';

// Define the structure of the groupConfig objects within attributes
export interface GroupConfigProps {
    id?: number;
     
    __component: string;
}

// Enum for relation types
enum RelationType {
    Ownership = 'ownership',
    Membership = 'membership',
    Inheritance = 'inheritance'
}

// Define the structure of the APIGroupConfig
export interface ApiGroupConfig {
    id: number;
    documentId?: string;
    groupConfig: GroupConfigProps[];
}

// Define the structure of GroupConfig object with arrays of groupConfig and userGroups
export interface GroupConfig {
    data: any;
    status(status: any): unknown;
    meta: any;
    id?: number;
    documentId?: string;
    userGroups?: Group[];
    groupConfig: GroupConfigProps;
}
export interface PostGroupConfigs {
    id?: number;
    groupConfig: GroupConfigProps[];
    userGroups?: string[];
}


// Define the structure of GroupConfigEnterprise
export interface GroupConfigEnterprise extends GroupConfigProps {
     
    __component: 'user.enterprise';
    name?: string;
    color?: string;
    logo?: string;
    memberSyncSource?: string;
    superAdminsGroup?: Group;
    superFleet?: Fleet;
    address?: Address;
    email?: string;
    phone?: string;
    contact?: Contact;
}

// Define the structure of GroupConfigHolding
export interface GroupConfigHolding extends GroupConfigProps {
     
    __component: 'holding.holding';
    name?: string;
    logo?: string;
    cover?: string;
    address?: Address;
    contact?: Contact;
    commercialInfos?: { companyNumber: string}
}

// Define the structure of GroupConfigOf
export interface GroupConfigOf extends GroupConfigProps {
     
    __component: 'user.of';
    targetModel?: string; // group, fleet, parking, userGroup ...
    targetId?: string;
    relation?: RelationType;
}

// Convert GroupConfig to ApiGroupConfig
const groupConfigToApiGroupConfig = (groupConfig: GroupConfig): PostGroupConfigs => {
    const { __component, ...rest } = groupConfig.groupConfig;
    if (__component === "user.enterprise") {
        rest.superAdminsGroup = (groupConfig.groupConfig as GroupConfigEnterprise).superAdminsGroup?.documentId;
    }
    const object: PostGroupConfigs =
    {
        groupConfig: [{ __component, ...rest }],
        id: groupConfig.id,
    };

    //if (object.groupConfig.at(0)?.__component) delete object.groupConfig[0].__component;
    if (groupConfig.userGroups) {
        object.userGroups = groupConfig?.userGroups.map(item => item.id?.toString()) as string[];

    }

    return object;
};

// Convert ApiGroupConfig to GroupConfig
const apiGroupConfigToGroupConfig = (apiGroupConfig: GroupConfigProps | GroupConfigEnterprise): GroupConfigProps | GroupConfigEnterprise => {
    const { apiGroupToGroup } = useGroupConvertor;
    const { apiFleetToFleet } = useFleetConvertor;

    switch (apiGroupConfig.__component) {
        case 'user.enterprise':
            return {
                ...apiGroupConfig,
                superAdminsGroup: (apiGroupConfig as GroupConfigEnterprise).superAdminsGroup ? apiGroupToGroup((apiGroupConfig as GroupConfigEnterprise).superAdminsGroup) : undefined,
                superFleet: (apiGroupConfig as GroupConfigEnterprise).superFleet ? apiFleetToFleet((apiGroupConfig as GroupConfigEnterprise).superFleet) : undefined
            } as GroupConfigEnterprise;
        case 'holding.holding':
            return {
                ...apiGroupConfig,
            };
        case 'user.of':
            return apiGroupConfig;
        default:
            return apiGroupConfig;
    }
};

// Convert array of ApiGroupConfig to array of GroupConfig
const apiGroupConfigsToGroupConfigs = (apiGroupConfigs?: ApiGroupConfig): GroupConfig | void => {
    if (apiGroupConfigs == null) {
        logTrace('apiGroupConfigsToGroupConfigs: apiGroupConfigs is null or undefined', SeverityLevel.Warning);
        return;
    }
    const convGrpCnf = apiGroupConfigs?.groupConfig?.at(0)
        ? apiGroupConfigToGroupConfig((apiGroupConfigs?.groupConfig?.at(0) as GroupConfigProps))
        : null;
    if (convGrpCnf) return { documentId: apiGroupConfigs?.documentId, groupConfig: convGrpCnf, id: apiGroupConfigs?.id };
};

const apiGroupConfigsMapToGroupConfigs = (apiGroupConfigs?: ApiGroupConfig[]): GroupConfig[] => {
    return apiGroupConfigs?.filter((item: ApiGroupConfig) => item.groupConfig?.length > 0)
        .map((item: ApiGroupConfig) => apiGroupConfigsToGroupConfigs(item)) as GroupConfig[];

};

const useGroupConfig = {
    apiGroupConfigToGroupConfig,
    apiGroupConfigsMapToGroupConfigs,
    apiGroupConfigsToGroupConfigs,
    groupConfigToApiGroupConfig,
};

export default useGroupConfig;